import React from "react";
import { Box, Collapse, CircularProgress, Typography } from "@mui/material";
import { ITextFieldSubmission } from "@Interfaces";
import TextFieldResponseItem from "./TextFieldResponseItem";
import { useTranslation } from "react-i18next";

interface Props {
    responses: ITextFieldSubmission[];
    isExpanded: boolean;
    isLoading: boolean;
    error?: string | null;
}

/**
 * TextFieldResponsesList
 *
 * Container component for displaying all submitted text field responses.
 * Handles loading, error, and empty states.
 * Renders individual response items in a collapsible section.
 *
 * Used in TextFieldCollectionCard footer for event managers.
 */
const TextFieldResponsesList: React.FC<Props> = ({
    responses,
    isExpanded,
    isLoading,
    error,
}) => {
    const { t } = useTranslation();

    return (
        <Collapse in={isExpanded}>
            <Box
                sx={{
                    mt: 1,
                    backgroundColor: "background.default",
                    borderRadius: 1,
                    overflow: "hidden",
                }}
            >
                {isLoading ? (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            p: 2,
                        }}
                    >
                        <CircularProgress size={24} />
                    </Box>
                ) : error ? (
                    <Box
                        sx={{
                            p: 2,
                            color: "error.main",
                            textAlign: "center",
                        }}
                    >
                        <Typography variant="body2">
                            {t("home.tabs.textfield_responses_error", {
                                defaultValue: "Error loading responses",
                            })}
                        </Typography>
                    </Box>
                ) : responses.length === 0 ? (
                    <Box
                        sx={{
                            p: 2,
                            color: "text.secondary",
                            textAlign: "center",
                        }}
                    >
                        <Typography variant="body2">
                            {t("home.tabs.textfield_no_responses", {
                                defaultValue: "No responses yet",
                            })}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Box
                            sx={{
                                p: 1,
                                px: 1.5,
                                borderBottom: "1px solid",
                                borderColor: "divider",
                                backgroundColor: "action.hover",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: "0.875rem",
                                    fontWeight: 500,
                                    color: "text.secondary",
                                }}
                            >
                                {responses.length}{" "}
                                {responses.length === 1
                                    ? "response"
                                    : "responses"}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                maxHeight: 500,
                                overflowY: "auto",
                                scrollBehavior: "smooth",
                            }}
                        >
                            {responses.map((response) => (
                                <TextFieldResponseItem
                                    key={response._id}
                                    response={response}
                                />
                            ))}
                        </Box>
                    </>
                )}
            </Box>
        </Collapse>
    );
};

export default TextFieldResponsesList;
